import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    RefreshCw, ChevronDown, CheckCircle2, BellRing, ExternalLink, CalendarClock, ClipboardPlus,
    XCircle, RotateCcw, Play, Check, UserCog,
} from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    SALES_AGENT_ALERTS_URL, SALES_AGENT_ALERT_ACTION_URL, SALES_AGENT_ALERTS_BULK_URL,
} from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { formatINR } from "../../components/dashboard/shared";
import {
    PriorityChip, STATUS_LABEL, ScopePicker, Freshness, EmptyState, openLeadState,
} from "./shared";

/**
 * Action Center -> Tracked alerts (trustedge sales_agent/).
 *
 * Every problem the alert rules found, ranked by the priority formula, with
 * its evidence and a recommended action. An alert stays until it is dealt
 * with: it resolves itself when the condition clears (a follow-up is booked,
 * the lead is contacted), and comes back if a "resolved" problem is still there.
 */

const STATUS_FILTERS = [
    ["open", "Open"], ["new", "New"], ["acknowledged", "Acknowledged"], ["in_progress", "In progress"],
    ["reopened", "Reopened"], ["closed", "Closed"], ["all", "All"],
];
const PRIORITIES = ["critical", "high", "medium", "low"];
const LABELS = {
    lead: "Lead", stage: "Stage", owner: "Owner", person: "Salesperson", deal_value: "Deal value",
    days_overdue: "Days overdue", followup_date: "Promised follow-up", days_idle: "Days with nothing logged",
    days_in_stage: "Days in stage", hours_unassigned: "Hours without owner", hours_waiting: "Hours since lead came in",
    overdue_followups: "Overdue follow-ups", threshold: "Threshold", postponements: "Times postponed",
    current_close_date: "Expected close", attempts: "Contact attempts", closed_on: "Closed on",
    close_date: "Expected close", days_to_close: "Days to close", achieved: "Achieved", target: "Target",
    achieved_pct: "Achieved %", month_elapsed_pct: "Month gone %", measure: "Measured on",
    missing: "Missing", phone_digits: "Digits in phone", high_value_threshold: "High-value amount",
};

function evidenceValue(key, value) {
    if (Array.isArray(value)) return value.join(", ");
    if (["deal_value", "high_value_threshold"].includes(key)) return formatINR(value);
    return String(value);
}

function AlertActions({ alert, canManage, onDone }) {
    const { create } = useApiMethods();
    const navigate = useNavigate();
    const [mode, setMode] = useState(null);       // 'dismiss' | 'reschedule' | 'task'
    const [text, setText] = useState("");
    const [when, setWhen] = useState(dayjs().add(1, "day").format("YYYY-MM-DD"));
    const [busy, setBusy] = useState(false);
    const open = ["new", "acknowledged", "in_progress", "reopened"].includes(alert.status);

    const act = async (action, extra = {}) => {
        setBusy(true);
        try {
            const res = await create(SALES_AGENT_ALERT_ACTION_URL(alert.id), { action, ...extra });
            if (res?.status === "success") { setMode(null); setText(""); onDone(res.data); }
            else ToastMsg("error", res?.message || "Could not update the alert");
        } finally { setBusy(false); }
    };

    const btn = "h-7 gap-1 px-2 text-xs";
    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
                {alert.customer_id && (
                    <Button size="sm" variant="outline" className={btn}
                        onClick={() => navigate("/customers/customerdetails", openLeadState(alert.customer_id))}>
                        <ExternalLink className="h-3 w-3" /> Open lead
                    </Button>
                )}
                {open && ["new", "reopened"].includes(alert.status) && (
                    <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => act("acknowledge")}>
                        <Check className="h-3 w-3" /> Acknowledge
                    </Button>
                )}
                {open && alert.status !== "in_progress" && (
                    <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => act("start")}>
                        <Play className="h-3 w-3" /> Start
                    </Button>
                )}
                {open && alert.customer_id && (
                    <Button size="sm" variant="outline" className={btn} onClick={() => setMode(mode === "reschedule" ? null : "reschedule")}>
                        <CalendarClock className="h-3 w-3" /> Reschedule follow-up
                    </Button>
                )}
                {open && !alert.task_id && (
                    <Button size="sm" variant="outline" className={btn} onClick={() => { setText(alert.recommended_action || ""); setMode(mode === "task" ? null : "task"); }}>
                        <ClipboardPlus className="h-3 w-3" /> Create task
                    </Button>
                )}
                {open && (
                    <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => act("resolve")}>
                        <CheckCircle2 className="h-3 w-3" /> Mark resolved
                    </Button>
                )}
                {open && canManage && (
                    <Button size="sm" variant="outline" className={btn} onClick={() => setMode(mode === "dismiss" ? null : "dismiss")}>
                        <XCircle className="h-3 w-3" /> Dismiss
                    </Button>
                )}
                {!open && canManage && (
                    <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => act("reopen")}>
                        <RotateCcw className="h-3 w-3" /> Reopen
                    </Button>
                )}
                {alert.task_id && (
                    <Button size="sm" variant="ghost" className={btn} onClick={() => navigate("/tasks")}>
                        Task #{alert.task_id}
                    </Button>
                )}
            </div>
            {mode === "dismiss" && (
                <div className="flex flex-wrap items-center gap-2">
                    <input value={text} onChange={e => setText(e.target.value)} placeholder="Why is this not a problem?"
                        className="h-8 min-w-[16rem] flex-1 rounded-md border border-gray-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800" />
                    <Button size="sm" className="h-8 text-xs" disabled={busy || !text.trim()}
                        onClick={() => act("dismiss", { reason: text })}>Dismiss</Button>
                    <span className="text-[11px] text-gray-400">It stays quiet for the snooze period set in Settings.</span>
                </div>
            )}
            {mode === "reschedule" && (
                <div className="flex flex-wrap items-center gap-2">
                    <input type="date" value={when} min={dayjs().format("YYYY-MM-DD")} onChange={e => setWhen(e.target.value)}
                        className="h-8 rounded-md border border-gray-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800" />
                    <input value={text} onChange={e => setText(e.target.value)} placeholder="Note (optional)"
                        className="h-8 min-w-[12rem] flex-1 rounded-md border border-gray-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800" />
                    <Button size="sm" className="h-8 text-xs" disabled={busy}
                        onClick={() => act("reschedule", { date: when, note: text })}>Save to lead timeline</Button>
                </div>
            )}
            {mode === "task" && (
                <div className="flex flex-wrap items-center gap-2">
                    <input value={text} onChange={e => setText(e.target.value)} placeholder="Task title"
                        className="h-8 min-w-[16rem] flex-1 rounded-md border border-gray-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800" />
                    <input type="date" value={when} onChange={e => setWhen(e.target.value)}
                        className="h-8 rounded-md border border-gray-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800" />
                    <Button size="sm" className="h-8 text-xs" disabled={busy || !text.trim()}
                        onClick={() => act("create_task", { title: text, due_date: when })}>
                        Create task for {alert.owner || "me"}
                    </Button>
                </div>
            )}
        </div>
    );
}

function AlertRow({ alert, expanded, onToggle, selectable, selected, onSelect, canManage, onChanged }) {
    const ev = Object.entries(alert.evidence || {}).filter(([k]) => LABELS[k]);
    const overdue = alert.due_date && dayjs(alert.due_date).isBefore(dayjs(), "day")
        && ["new", "acknowledged", "in_progress", "reopened"].includes(alert.status);
    return (
        <li className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start gap-2 px-3 py-2">
                {selectable && (
                    <input type="checkbox" className="mt-1" checked={selected} onChange={onSelect}
                        aria-label={`Select ${alert.title}`} />
                )}
                <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-start gap-2 text-left">
                    <PriorityChip priority={alert.priority} score={alert.priority_score} />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{alert.title}</p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{alert.problem}</p>
                    </div>
                    <div className="hidden shrink-0 text-right text-[11px] text-gray-500 sm:block">
                        <p>{alert.owner || alert.person || "Managers"}</p>
                        <p className={cn(overdue && "font-medium text-rose-600")}>
                            {alert.due_date ? `Due ${dayjs(alert.due_date).format("DD MMM")}` : ""}
                        </p>
                    </div>
                    <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {STATUS_LABEL[alert.status] || alert.status}
                    </span>
                    <ChevronDown className={cn("mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform", expanded && "rotate-180")} />
                </button>
            </div>
            {expanded && (
                <div className="space-y-3 border-t border-gray-100 px-3 py-3 dark:border-gray-700">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Recommended action</p>
                        <p className="text-sm text-gray-800 dark:text-gray-100">{alert.recommended_action}</p>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Evidence</p>
                            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
                                {ev.map(([k, v]) => (
                                    <div key={k} className="contents">
                                        <dt className="text-gray-500">{LABELS[k]}</dt>
                                        <dd className="text-gray-800 dark:text-gray-200">{evidenceValue(k, v)}</dd>
                                    </div>
                                ))}
                            </dl>
                            <p className="mt-1 text-[11px] text-gray-400">
                                Rule: {alert.rule} · first seen {dayjs(alert.first_detected_at).format("DD MMM hh:mm A")}
                                {alert.detections > 1 && ` · seen ${alert.detections} times`}
                                {alert.confidence !== "high" && ` · confidence ${alert.confidence}`}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Why priority {alert.priority_score}
                            </p>
                            {Object.entries(alert.priority_breakdown || {}).map(([k, b]) => (
                                <div key={k} className="flex items-center gap-2 text-xs">
                                    <span className="w-20 capitalize text-gray-500">{k.replace("_", " ")}</span>
                                    <div className="h-1.5 flex-1 rounded bg-gray-100 dark:bg-gray-700">
                                        <div className="h-1.5 rounded bg-primary" style={{ width: `${Math.min(100, (b.points / (b.weight || 1)) * 100)}%` }} />
                                    </div>
                                    <span className="w-14 text-right tabular-nums text-gray-600 dark:text-gray-300">{b.points}/{b.weight}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {alert.dismissed_reason && <p className="text-xs text-gray-500">Dismissed: {alert.dismissed_reason}</p>}
                    {alert.resolution === "condition_cleared" && (
                        <p className="text-xs text-emerald-600">Resolved by itself: the condition cleared on {dayjs(alert.resolved_at).format("DD MMM hh:mm A")}.</p>
                    )}
                    {alert.note && <pre className="whitespace-pre-wrap font-sans text-xs text-gray-500">{alert.note}</pre>}
                    <AlertActions alert={alert} canManage={canManage} onDone={onChanged} />
                </div>
            )}
        </li>
    );
}

export default function AlertsPanel() {
    const { fetch, create } = useApiMethods();
    const [params] = useSearchParams();
    const focus = params.get("alert");
    const [filters, setFilters] = useState({ status: focus ? "all" : "open", priority: [], rule: "", owner: "", q: "", scope: "" });
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(focus ? Number(focus) : null);
    const [selected, setSelected] = useState([]);

    const load = useCallback(() => {
        setLoading(true);
        const query = { status: filters.status, page };
        if (filters.priority.length) query.priority = filters.priority.join(",");
        if (filters.rule) query.rule = filters.rule;
        if (filters.owner === "mine") query.mine = 1;
        else if (filters.owner) query.owner = filters.owner;
        if (filters.q) query.q = filters.q;
        if (filters.scope) query.scope = filters.scope;
        fetch(SALES_AGENT_ALERTS_URL, query)
            .then(res => {
                if (res?.status === "success") { setData(res.data); setSelected([]); }
                else ToastMsg("error", res?.message || "Could not load alerts");
            })
            .finally(() => setLoading(false));
    }, [filters, page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const set = (key, value) => { setPage(1); setFilters(f => ({ ...f, [key]: value })); };
    const togglePriority = (p) => set("priority", filters.priority.includes(p)
        ? filters.priority.filter(x => x !== p) : [...filters.priority, p]);
    const replace = (row) => setData(d => ({ ...d, results: d.results.map(r => r.id === row.id ? row : r) }));
    const ackable = (data?.results || []).filter(r => ["new", "reopened"].includes(r.status));

    const bulkAck = async () => {
        const res = await create(SALES_AGENT_ALERTS_BULK_URL, { action: "acknowledge", ids: selected });
        if (res?.status === "success") { ToastMsg("success", `${res.data.updated.length} acknowledged`); load(); }
        else ToastMsg("error", res?.message || "Could not acknowledge");
    };

    const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
    return (
        <ContainerCard>
            <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                            <BellRing className="h-4 w-4 text-primary" /> Tracked alerts
                        </h2>
                        {data && <Freshness calculatedAt={null} rulesAt={data.rules_last_evaluated}
                            extra={<span>{data.scope?.label}</span>} />}
                    </div>
                    <div className="flex items-center gap-2">
                        <ScopePicker scopes={data?.scopes} value={filters.scope || data?.scope?.key}
                            onChange={v => set("scope", v)} />
                        <Button onClick={load} size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <select value={filters.status} onChange={e => set("status", e.target.value)} aria-label="Status"
                        className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs dark:border-gray-600 dark:bg-gray-800">
                        {STATUS_FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    {PRIORITIES.map(p => (
                        <button key={p} type="button" onClick={() => togglePriority(p)} aria-pressed={filters.priority.includes(p)}
                            className={cn("h-8 rounded-md border px-2 text-xs capitalize",
                                filters.priority.includes(p) ? "border-primary bg-primary/10 text-primary" : "border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300")}>
                            {p}
                        </button>
                    ))}
                    <select value={filters.rule} onChange={e => set("rule", e.target.value)} aria-label="Alert type"
                        className="h-8 max-w-[14rem] rounded-md border border-gray-300 bg-white px-2 text-xs dark:border-gray-600 dark:bg-gray-800">
                        <option value="">All alert types</option>
                        {(data?.rules || []).map(r => <option key={r.key} value={r.key}>{r.name} ({r.open})</option>)}
                    </select>
                    <select value={filters.owner} onChange={e => set("owner", e.target.value)} aria-label="Owner"
                        className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs dark:border-gray-600 dark:bg-gray-800">
                        <option value="">Everyone</option>
                        <option value="mine">Assigned to me</option>
                        <option value="none">For managers (no owner)</option>
                    </select>
                    <input value={filters.q} onChange={e => set("q", e.target.value)} placeholder="Search"
                        className="h-8 w-40 rounded-md border border-gray-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800" />
                    {data?.can_manage && selected.length > 0 && (
                        <Button size="sm" className="h-8 text-xs" onClick={bulkAck}>Acknowledge {selected.length}</Button>
                    )}
                </div>

                {!data && loading && (
                    <div className="space-y-2">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
                )}
                {data && data.results.length === 0 && (
                    <EmptyState icon={CheckCircle2} title="Nothing here">
                        {filters.status === "open" ? "No open alerts match these filters. The rules run every 30 minutes." : "No alerts match these filters."}
                    </EmptyState>
                )}
                {data && data.results.length > 0 && (
                    <>
                        {data.can_manage && ackable.length > 0 && (
                            <label className="flex items-center gap-2 text-[11px] text-gray-500">
                                <input type="checkbox" checked={selected.length === ackable.length}
                                    onChange={e => setSelected(e.target.checked ? ackable.map(r => r.id) : [])} />
                                Select all new on this page
                            </label>
                        )}
                        <ul className="space-y-2">
                            {data.results.map(a => (
                                <AlertRow key={a.id} alert={a} expanded={expanded === a.id}
                                    onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
                                    selectable={data.can_manage && ["new", "reopened"].includes(a.status)}
                                    selected={selected.includes(a.id)}
                                    onSelect={() => setSelected(s => s.includes(a.id) ? s.filter(x => x !== a.id) : [...s, a.id])}
                                    canManage={data.can_manage} onChanged={replace} />
                            ))}
                        </ul>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{data.total} alert{data.total === 1 ? "" : "s"}</span>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                                <span>{page} / {pages}</span>
                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</Button>
                            </div>
                        </div>
                    </>
                )}
                <p className="flex items-center gap-1 text-[11px] text-gray-400">
                    <UserCog className="h-3 w-3" /> Alerts are raised by fixed rules (Settings → Sales Agent). Nothing here contacts a customer.
                </p>
            </div>
        </ContainerCard>
    );
}
